import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  User,
  ChevronRight,
  Download,
  Star,
  DollarSign,
  Gift,
  CheckCircle2,
  Award,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Globe
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SequencerCardProps {
  userId: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  sequenceCount: number;

  // Enhanced fields
  totalDownloads?: number;
  averageRating?: number;
  freeSequenceCount?: number;
  paidSequenceCount?: number;
  bio?: string | null;
  socialLinks?: Record<string, string> | null;
  featured?: boolean;
  verified?: boolean;
  createdAt?: string;

  // Legacy fields (for backward compatibility with existing usage)
  holiday?: string;
  software?: string;
}

/**
 * Enhanced sequencer (creator) card component for displaying comprehensive creator information.
 *
 * Features:
 * - Creator avatar (with fallback)
 * - Name, bio snippet, and join date
 * - Sequence count, downloads, and rating
 * - Free vs paid sequence breakdown
 * - Featured and verified badges
 * - Social media links
 * - Navigation to dedicated creator profile page
 *
 * Backward compatible: If enhanced fields not provided, falls back to legacy behavior.
 */
export const SequencerCard: React.FC<SequencerCardProps> = ({
  userId,
  displayName,
  username,
  avatarUrl,
  sequenceCount,
  totalDownloads = 0,
  averageRating = 0,
  freeSequenceCount = 0,
  paidSequenceCount = 0,
  bio,
  socialLinks,
  featured = false,
  verified = false,
  createdAt,
  holiday,
  software,
}) => {
  const creatorName = displayName || username || 'Unknown Creator';

  // Determine navigation href
  // If username is available, go to dedicated profile page
  // Otherwise, fall back to legacy filtered search behavior
  let href: string;
  if (username && !holiday && !software) {
    // Enhanced mode: Direct link to creator profile
    href = `/sequencer/${username}`;
  } else {
    // Legacy mode: Link to filtered search
    const params = new URLSearchParams();
    params.set('sequences_v2[refinementList][creator_name][0]', creatorName);
    if (holiday) {
      params.set('sequences_v2[refinementList][holiday_type][0]', holiday);
    }
    if (software) {
      params.set('sequences_v2[refinementList][software_type][0]', software);
    }
    href = `/sequences/search?${params.toString()}`;
  }

  // Get initials for fallback avatar
  const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Format join date
  const formatJoinDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  // Get bio snippet (first 2 lines or 100 characters)
  const getBioSnippet = (text: string) => {
    const lines = text.split('\n').filter((line) => line.trim());
    const firstTwoLines = lines.slice(0, 2).join(' ');
    if (firstTwoLines.length > 100) {
      return firstTwoLines.substring(0, 97) + '...';
    }
    return firstTwoLines;
  };

  // Social media icon mapping
  const getSocialIcon = (platform: string) => {
    const lower = platform.toLowerCase();
    if (lower.includes('twitter') || lower.includes('x.com')) return Twitter;
    if (lower.includes('facebook')) return Facebook;
    if (lower.includes('instagram')) return Instagram;
    if (lower.includes('youtube')) return Youtube;
    return Globe;
  };

  // Determine if this is "enhanced mode" (has new stats)
  const isEnhancedMode = totalDownloads > 0 || averageRating > 0 || bio || featured || verified;

  return (
    <Link to={href}>
      <Card className="group hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer h-full">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            {/* Header: Avatar, Name, and Badges */}
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {/* Avatar */}
                <div className="relative w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-purple-500/20 to-pink-500/10 group-hover:from-purple-500/30 group-hover:to-pink-500/20 transition-all flex items-center justify-center shrink-0">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={creatorName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <div
                    className={cn(
                      'absolute inset-0 flex items-center justify-center',
                      avatarUrl && 'opacity-0'
                    )}
                  >
                    {creatorName !== 'Unknown Creator' ? (
                      <span className="text-base font-semibold text-purple-600">
                        {getInitials(creatorName)}
                      </span>
                    ) : (
                      <User className="h-7 w-7 text-purple-600" />
                    )}
                  </div>
                </div>

                {/* Name and Badges */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                      {creatorName}
                    </h3>
                    {featured && (
                      <Badge variant="default" className="shrink-0 gap-1">
                        <Award className="h-3 w-3" />
                        Featured
                      </Badge>
                    )}
                    {verified && (
                      <Badge variant="secondary" className="shrink-0 gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  {/* Join Date (if available) */}
                  {createdAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Member since {formatJoinDate(createdAt)}
                    </p>
                  )}
                </div>
              </div>

              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </div>

            {/* Bio Snippet (if available and in enhanced mode) */}
            {isEnhancedMode && bio && (
              <p className="text-sm text-muted-foreground line-clamp-2">{getBioSnippet(bio)}</p>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Sequences */}
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-primary">{sequenceCount}</div>
                <div className="text-xs text-muted-foreground">
                  {sequenceCount === 1 ? 'Sequence' : 'Sequences'}
                </div>
              </div>

              {/* Downloads (if available) */}
              {isEnhancedMode && totalDownloads > 0 && (
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <div className="font-semibold">{totalDownloads.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Downloads</div>
                  </div>
                </div>
              )}

              {/* Average Rating (if available) */}
              {isEnhancedMode && averageRating > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 shrink-0" />
                  <div>
                    <div className="font-semibold">{averageRating.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Rating</div>
                  </div>
                </div>
              )}

              {/* Free/Paid Breakdown (if available) */}
              {isEnhancedMode && (freeSequenceCount > 0 || paidSequenceCount > 0) && (
                <div className="col-span-2 flex gap-4 text-xs">
                  {freeSequenceCount > 0 && (
                    <div className="flex items-center gap-1">
                      <Gift className="h-3 w-3 text-green-600" />
                      <span className="font-medium">{freeSequenceCount}</span>
                      <span className="text-muted-foreground">Free</span>
                    </div>
                  )}
                  {paidSequenceCount > 0 && (
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-blue-600" />
                      <span className="font-medium">{paidSequenceCount}</span>
                      <span className="text-muted-foreground">Premium</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Social Links (if available) */}
            {isEnhancedMode && socialLinks && Object.keys(socialLinks).length > 0 && (
              <div className="flex gap-2 pt-2 border-t">
                {Object.entries(socialLinks).slice(0, 4).map(([platform, url]) => {
                  const Icon = getSocialIcon(platform);
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-full hover:bg-accent transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
